/**
 * KAN-961: заголовок страницы живёт в верхней строке кабинета.
 *
 * PageLayout регистрирует title, subtitle и хлебные крошки через сеттер,
 * CabinetTopBar читает их из контекста данных. Контекстов два: сеттер
 * стабилен, поэтому запись заголовка не перерисовывает саму страницу.
 * Вне PageHeaderProvider сеттера нет: PageLayout рисует заголовок у себя.
 */
import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';

export interface PageHeaderCrumb {
  label: string;
  href?: string;
  'data-testid'?: string;
}

export interface PageHeaderData {
  title: string;
  /**
   * Рендерится в верхней строке, то есть вне провайдеров, которые страница
   * ставит у себя: элемент не должен читать контексты страницы.
   */
  subtitle?: ReactNode;
  breadcrumbs?: PageHeaderCrumb[];
}

interface PageHeaderEntry extends PageHeaderData {
  owner: symbol;
}

export interface PageHeaderSetter {
  set: (entry: PageHeaderEntry) => void;
  clear: (owner: symbol) => void;
}

export const PageHeaderDataContext = createContext<PageHeaderData | null>(null);
export const PageHeaderSetterContext = createContext<PageHeaderSetter | null>(null);

/** Данные заголовка текущей страницы (null: страница не зарегистрировалась). */
export function usePageHeaderData(): PageHeaderData | null {
  return useContext(PageHeaderDataContext);
}

/** true: есть верхняя строка, куда уходит заголовок страницы. */
export function useHasPageHeaderSlot(): boolean {
  return useContext(PageHeaderSetterContext) !== null;
}

/**
 * Регистрирует заголовок страницы в верхней строке. Обновляется на каждом
 * рендере страницы (subtitle бывает элементом), снимается при размонтировании;
 * снимает только свою запись, чтобы не стереть заголовок следующей страницы.
 */
export function useRegisterPageHeader(data: PageHeaderData, enabled: boolean): void {
  const setter = useContext(PageHeaderSetterContext);
  const ownerRef = useRef<symbol>(Symbol('page-header'));
  const { title, subtitle, breadcrumbs } = data;

  useLayoutEffect(() => {
    if (!setter || !enabled) return;
    setter.set({ owner: ownerRef.current, title, subtitle, breadcrumbs });
  });

  useLayoutEffect(() => {
    if (!setter || !enabled) return undefined;
    const owner = ownerRef.current;
    return () => setter.clear(owner);
  }, [setter, enabled]);
}
